import { useState } from "react";

import BlockIcon from "@mui/icons-material/Block";
import FlagIcon from "@mui/icons-material/Flag";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Zoom,
} from "@mui/material";

import { ReportResourceModal } from "components/ReportResourceModal";
import { BlockCarrierModal } from "components/Shipper/components/BlockCarrierButton";
import { CarrierBlockedSource } from "constants/index";
import { useDisclosure, useRoleContext } from "hooks";

interface QuestionAuthor {
  carrierRoleId: number;
  companyName: string;
  firstName?: string;
  lastName?: string;
}

interface Props {
  questionId: number;
  // Set only for viewers allowed to see the question's author: carriers never
  // learn who a peer is, so this stays unset for them.
  author?: QuestionAuthor;
}

export function ShipmentQuestionActionsMenu(props: Props) {
  const { questionId, author } = props;
  const { role } = useRoleContext();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const {
    isOpen: reportModalOpen,
    onClose: onCloseReportModal,
    onOpen: onOpenReportModal,
  } = useDisclosure(false);
  const {
    isOpen: blockModalOpen,
    onClose: onCloseBlockModal,
    onOpen: onOpenBlockModal,
  } = useDisclosure(false);

  // Blocking is a shipper-only action against a carrier. Gated on the role and
  // not merely on author data being present, so exposing author fields to
  // carriers later cannot hand them a block action.
  const canBlockCarrier = role?.type === "SHIPPER" && author !== undefined;

  const onCloseMenu = () => setMenuAnchor(null);

  const onReportClicked = () => {
    onCloseMenu();
    onOpenReportModal();
  };

  const onBlockClicked = () => {
    onCloseMenu();
    onOpenBlockModal();
  };

  return (
    <>
      <Tooltip
        title="More actions for this question"
        placement="top"
        TransitionComponent={Zoom}
      >
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          size="medium"
          aria-label="More actions for this question"
        >
          {/* Explicit size: the theme's medium (~21px) and large (30px) leave no
              step in between, and this sits one step below large. */}
          <MoreVertIcon sx={{ color: "black", fontSize: 24 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={onCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Tooltip
          describeChild
          title="Flag this question for our team to review"
          placement="left"
        >
          <MenuItem onClick={onReportClicked}>
            <ListItemIcon>
              <FlagIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Report question" />
          </MenuItem>
        </Tooltip>
        {canBlockCarrier && (
          // Separated and red-texted so blocking reads as destructive, without
          // a filled warning banner shouting at a routine menu.
          <Box sx={{ mt: 0.5, pt: 0.5, borderTop: 1, borderColor: "divider" }}>
            <Tooltip
              describeChild
              title="Stop this carrier from seeing and quoting on your shipments"
              placement="left"
            >
              <MenuItem onClick={onBlockClicked}>
                <ListItemIcon>
                  <BlockIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Block carrier"
                  primaryTypographyProps={{ color: "error" }}
                />
              </MenuItem>
            </Tooltip>
          </Box>
        )}
      </Menu>
      <ReportResourceModal
        open={reportModalOpen}
        onClose={onCloseReportModal}
        resourceType="SHIPMENT_QA_QUESTION"
        resourceId={questionId}
      />
      {canBlockCarrier && (
        <BlockCarrierModal
          open={blockModalOpen}
          onClose={onCloseBlockModal}
          carrierId={author.carrierRoleId}
          companyName={author.companyName}
          firstName={author.firstName}
          lastName={author.lastName}
          source={CarrierBlockedSource.SHIPMENT_QUESTION}
        />
      )}
    </>
  );
}
